import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {AbstractInputDirective} from '../AbstractInputDirective';
import {Subject} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';

@Component({
  selector: 'digires-group-multi-input',
  templateUrl: './group-multi-input.component.html',
  styleUrls: ['./group-multi-input.component.scss']
})
export class GroupMultiInputComponent extends AbstractInputDirective implements OnInit, OnDestroy, OnChanges {
  destroy$: Subject<boolean> = new Subject<boolean>();

  public readonly NO_ITEMS = {dummy: -1};
  public readonly ALL_ITEMS = {dummy: 1};

  internalFormGroup: FormGroup;
  aggregatedItems: any[] = [];
  prevSelectedItems: any[];
  isPatchingFg: boolean = false;
  @Input() allItemName: string;
  @Input() noItemName: string;
  @Input() groups: {label: string, items: any[]}[] = [];
  @Input() itemToId: ((value: any) => any) = value => value;
  @Input() itemToLabel: ((value: any) => string) = value => value;

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.groups?.currentValue) {
      this.aggregatedItems = this.groups.flatMap(g => g.items);
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.internalFormGroup = new FormGroup({multiSelect: new FormControl()});
    this.getCtrl()
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(newValue => {
        if (!this.isPatchingFg) {
          this.syncFormGroups(newValue);
        }
      });
    this.syncFormGroups(this.getValue());
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  getReadonlyValue(): string {
    const formValue = this.getValue();
    if (!formValue) {
      return null;
    }
    const formValues: any[] = formValue;
    const itemValues: any[] = this.aggregatedItems.filter(item => formValues.includes(this.itemToId(item)));
    return itemValues.map(item => this.itemToLabel(item)).join(', ');
  }

  private syncFormGroups(newValue: any) {
    this.isPatchingFg = true;
    const currentValue = newValue
      ? newValue.map(v => this.itemToId(v))
      : this.aggregatedItems.map(v => this.itemToId(v));
    if (newValue !== currentValue) {
      this.patch(currentValue);
    }
    if (currentValue && currentValue.length === 0 && this.noItemName) {
      this.prevSelectedItems = [this.NO_ITEMS];
    } else if (currentValue.length === this.aggregatedItems.length && this.allItemName) {
      this.prevSelectedItems = [this.ALL_ITEMS];
    } else {
      this.prevSelectedItems = currentValue;
    }
    this.internalFormGroup.patchValue({multiSelect: this.prevSelectedItems});
    this.isPatchingFg = false;
  }

  onItemSelected(event: MatSelectChange): void {
    this.isPatchingFg = true;
    if (event.value.includes(this.ALL_ITEMS) && !this.prevSelectedItems.includes(this.ALL_ITEMS)) {
      // ALL_ITEMS has just been selected => unselect everything else

      this.internalFormGroup.patchValue({multiSelect: [this.ALL_ITEMS]});
      this.patch(this.aggregatedItems.map(v => this.itemToId(v)));

    } else if (event.value.includes(this.NO_ITEMS) && !this.prevSelectedItems.includes(this.NO_ITEMS)) {
      // NO_ITEMS has just been selected => unselect everything else

      this.internalFormGroup.patchValue({multiSelect: [this.NO_ITEMS]});
      this.patch([]);

    } else if (event.value.length === 0) {
      // no item has been selected => either select NO_ITEMS or ALL_ITEMS

      if (this.noItemName) {
        this.internalFormGroup.patchValue({multiSelect: [this.NO_ITEMS]});
        this.patch([]);
      } else if (this.allItemName) {
        this.internalFormGroup.patchValue({multiSelect: [this.ALL_ITEMS]});
        this.patch(this.aggregatedItems.map(v => this.itemToId(v)));
      } else {
        this.patch(this.internalFormGroup.value.multiSelect);
      }

    } else if (event.value.length > 1 && (event.value.includes(this.ALL_ITEMS) || event.value.includes(this.NO_ITEMS))) {
      // selected another item in addition to ALL_ITEMS or NO_ITEMS => unselect ALL_ITEMS and NO_ITEMS

      const filteredItems = event.value.filter(item => item !== this.NO_ITEMS && item !== this.ALL_ITEMS);
      this.internalFormGroup.patchValue({multiSelect: filteredItems});
      this.patch(filteredItems.map(v => this.itemToId(v)));

    } else if (event.value.length === this.aggregatedItems.length && this.allItemName) {
      // all items have been selected manually => select ALL_ITEMS
      // (NO_ITEMS and ALL_ITEMS can not be included in the value because of previous cases)

      this.internalFormGroup.patchValue({multiSelect: [this.ALL_ITEMS]});
      this.patch(this.aggregatedItems.map(v => this.itemToId(v)));

    } else {
      // just patch through to external FormGroup
      this.patch(this.internalFormGroup.value.multiSelect);

    }
    this.prevSelectedItems = this.internalFormGroup.value.multiSelect;
    this.isPatchingFg = false;
  }

}
